import FileUpload from "../../components/common/FileUpload";
import CustomInput from "../../components/CustomInput";
import SubHeadings from "../../components/SubHeadings";
import { useFormEdit } from "../../util/hooks/useFormEdit";
import { SchoolBranch } from "../../types/schoolBranchType";
import { useEffect, useState } from "react";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setBranch } from "../../features/access-controller/AccessController";

interface Props {
  branch: SchoolBranch | undefined;
  isLoading: boolean;
  onDataChange: (data: Partial<SchoolBranch>) => void;
}

export default function SchoolDetailsForm({
  branch,
  isLoading,
  onDataChange,
}: Props) {
  const { formData, setFormData, isEditing } = useFormEdit<SchoolBranch>(
    {} as SchoolBranch,
  );

  const [logoChanged, setLogoChanged] = useState(false);
  const dispatch = useDispatch();

  const handleInputChange = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      onDataChange(updated);
      return updated;
    });
  };

  useEffect(() => {
    if (branch) {
      setFormData(branch);
    }
  }, [branch, setFormData]);

  const handleLogoUpload = (url: string) => {
    const cleanUrl = url.replace(/^"+|"+$/g, "");

    setFormData((prev) => {
      const updated = { ...prev, schoolBranchIcon: cleanUrl };
      onDataChange(updated);
      return updated;
    });

    setLogoChanged(true);
  };

  const handleSaveLogo = async () => {
    if (!branch?.schoolBranchId || !formData.schoolBranchIcon) return;

    try {
      await SchoolBranchApis.changeBranchIcon(
        branch.schoolBranchId,
        formData.schoolBranchIcon,
      );

      dispatch(
        setBranch({
          ...branch,
          schoolBranchIcon: formData.schoolBranchIcon,
        }),
      );

      toast.success("Logo updated successfully");
      setLogoChanged(false);
    } catch (error) {
      toast.error("Failed to update logo");
    }
  };

  return (
    <div className="px-4 md:px-9 flex flex-col-reverse md:flex-row md:divide-x divide-neutral-50 gap-9">
      <div className="flex flex-col gap-7 w-full py-7">
        <SubHeadings text="School profile" />
        <form className="grid gap-7">
          <CustomInput
            name="schoolBranchName"
            label="School Name"
            readOnly={!isEditing}
            type="text"
            placeholder="Enter School Name"
            value={formData?.schoolBranchName ?? ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
          <CustomInput
            name="emailAddress"
            label="School Email address"
            readOnly={!isEditing}
            type="text"
            placeholder="Enter Email address"
            value={formData?.emailAddress ?? ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="primaryPhone"
              label="School Phone"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter Phone Number"
              value={formData?.primaryPhone ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="secondaryPhone"
              label="Secondary phone"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter Secondary phone"
              value={formData?.secondaryPhone ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>

          <CustomInput
            name="address"
            label="Address"
            type="text"
            placeholder="Enter Address"
            readOnly={!isEditing}
            value={formData?.address ?? ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="city"
              label="City"
              type="text"
              placeholder="Enter City"
              readOnly={!isEditing}
              value={formData?.city ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="area"
              label="Area"
              type="text"
              placeholder="Enter Area"
              readOnly={!isEditing}
              value={formData?.area ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="state"
              label="State"
              type="text"
              placeholder="Enter State"
              readOnly={!isEditing}
              value={formData?.state ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="country"
              label="Country"
              type="text"
              placeholder="Enter Country"
              readOnly={!isEditing}
              value={formData?.country ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="schoolCode"
              label="School Code"
              type="text"
              placeholder="Enter School Code"
              readOnly={!isEditing}
              value={formData?.schoolCode ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="schoolWebsite"
              label="School Website"
              type="text"
              placeholder="Enter School Website"
              readOnly={!isEditing}
              value={formData?.schoolWebsite ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>
        </form>
      </div>

      <div className="md:pl-7 pt-7 md:py-7">
        <div className="md:pl-7 pt-7 md:py-7 flex flex-col gap-4">
          <FileUpload
            value={formData?.schoolBranchIcon ?? ""}
            onUploadComplete={handleLogoUpload}
            className="md:w-52 w-36 aspect-square"
            disabled={!isEditing}
            showButton={!logoChanged}
            buttonText="upload Logo"
            uploadText="Change Logo"
          />

          {isEditing && logoChanged && (
            <button
              type="button"
              onClick={handleSaveLogo}
              className="bg-primary-500 text-white rounded-lg py-2 px-4 text-sm font-semibold"
            >
              Save Logo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
